import { useTranslation } from 'react-i18next';
import { useState, useMemo } from 'react';

import { useAppSelector, useAppDispatch } from 'hooks';
import { FlagIcon } from 'components/icons/FlagIcon';
import { ProductTypeCodes } from 'entities/progress';
import Modal from 'components/Modal';
import Steps from 'components/Steps';
import {
  getActiveProductsTypeCodes,
  getProcent,
} from 'pages/dashboard/components/Progress/selectors';

import { setIsModalOpened, setStep } from '../submitDashboardSlice';
import { getIsModalOpened, getStep } from '../selectors';
import InformationReceived from './InformationReceived';
import { submitApplication } from '../thunks';
import Confirmation from './Confirmation';
import ModalActions from './ModalActions';
import LineOfCredit from './LineOfCredit';
import AcceptTerms from './AcceptTerms';
import Connectors from './Connectors';

const SubmitModal = ({ onWarning }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('dashboard');

  const isModalOpened = useAppSelector(getIsModalOpened);
  const currentStep = useAppSelector(getStep);
  const appPercent = useAppSelector(getProcent);
  const activeProductTypes = useAppSelector(getActiveProductsTypeCodes);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const showConnectorsStep = activeProductTypes.includes(
    ProductTypeCodes.payments,
  );
  const showLineOfCredit = activeProductTypes.includes(
    ProductTypeCodes.capital,
  );

  const stepsLength = useMemo(() => {
    let steps = 2;

    if (showConnectorsStep) {
      steps += 2;
    }

    if (showLineOfCredit) {
      steps += 1;
    }

    return steps;
  }, [showConnectorsStep, showLineOfCredit]);

  const handleClose = () => {
    dispatch(setIsModalOpened(false));
  };

  const handleBack = () => {
    dispatch(setStep(currentStep - 1));
  };

  const handleNext = () => {
    dispatch(setStep(currentStep + 1));
  };

  const handleSubmit = async () => {
    if (!appPercent || appPercent < 100) {
      handleClose();
      onWarning();
      return;
    }

    setIsLoading(true);

    const result = await dispatch(submitApplication());

    if (result.payload) {
      handleNext();
    }

    setIsLoading(false);
  };

  return (
    <Modal
      isOpen={isModalOpened}
      closeModal={handleClose}
      title={t('dashboard:submitModal.title')}
      icon={<FlagIcon className="text-gray-600" />}
      isOverlayDisabled={true}
      actions={
        <ModalActions
          step={currentStep}
          handleSubmit={handleSubmit}
          handleBack={handleBack}
          handleNext={handleNext}
          isLoading={isLoading}
        />
      }
      maxWidth="1280px"
      variant="screen"
      zIndex={25}
    >
      <div className="h-full flex flex-col">
        {showConnectorsStep ||
          (currentStep <= stepsLength && (
            <div className="pt-6 px-6">
              <Steps step={currentStep} stepsLength={stepsLength} />
            </div>
          ))}

        {currentStep === 1 &&
          (showConnectorsStep ? <Connectors /> : <Confirmation />)}
        {currentStep === 2 &&
          (showConnectorsStep ? <Confirmation /> : <AcceptTerms />)}
        {currentStep === 3 &&
          (showConnectorsStep ? (
            <AcceptTerms />
          ) : showLineOfCredit ? (
            <LineOfCredit />
          ) : (
            <InformationReceived />
          ))}
        {currentStep === 4 &&
          (showConnectorsStep && showLineOfCredit ? (
            <LineOfCredit />
          ) : (
            <InformationReceived />
          ))}
        {currentStep === 5 && <InformationReceived />}
      </div>
    </Modal>
  );
};

export default SubmitModal;
